/**
 * API Error Handler
 * 
 * Handles errors from API calls with support for:
 * - Standard HTTP errors
 * - .NET backend error responses (when implemented)
 * - Network failures
 * - Timeout errors
 * 
 * This handler is backend-agnostic but optimized for .NET responses
 */

import type { AppError, ErrorCategory, ErrorSeverity } from '@/types/error.types';

/**
 * Standard API Error Response (matches .NET backend format)
 * When backend is implemented, it should return this structure
 */
export interface ApiErrorResponse {
  /** Error type/code */
  type?: string;
  
  /** User-friendly error message */
  title?: string;
  
  /** HTTP status code */
  status?: number;
  
  /** Detailed error message */
  detail?: string;
  
  /** Trace ID for logging/support */
  traceId?: string;
  
  /** Validation errors (for 400 Bad Request) */
  errors?: Record<string, string[]>;
  
  /** Additional metadata */
  [key: string]: any;
}

/**
 * Parse error response from API
 * Handles both current state (no backend) and future .NET backend
 */
export async function parseApiError(response: Response): Promise<ApiErrorResponse> {
  const contentType = response.headers.get('content-type');
  
  try {
    // Try to parse JSON response (standard for .NET APIs)
    if (contentType?.includes('application/json')) {
      const data = await response.json();
      
      // Check if it matches our standard error format
      if (data && typeof data === 'object') {
        return {
          type: data.type || data.errorType || 'ApiError',
          title: data.title || data.message || data.error,
          status: data.status || response.status,
          detail: data.detail || data.details || data.errorMessage,
          traceId: data.traceId || data.correlationId,
          errors: data.errors || data.validationErrors,
          ...data,
        };
      }
    }
    
    // Fallback: try to get text response
    const text = await response.text();
    return {
      type: 'ApiError',
      title: response.statusText || 'Request Failed',
      status: response.status,
      detail: text || undefined,
    };
  } catch (parseError) {
    // If we can't parse the response, return basic info
    return {
      type: 'ApiError',
      title: response.statusText || 'Request Failed',
      status: response.status,
      detail: 'Unable to parse error response',
    };
  }
}

/**
 * Map HTTP status codes to error categories
 */
function getErrorCategory(status: number): ErrorCategory {
  if (status === 401) return 'authentication';
  if (status === 403) return 'authorization';
  if (status === 404) return 'data';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'system';
  return 'network';
}

/**
 * Map HTTP status codes to error severity
 */
function getErrorSeverity(status: number): ErrorSeverity {
  if (status === 401 || status === 403) return 'error';
  if (status === 404) return 'warning';
  if (status === 400 || status === 422) return 'warning';
  if (status >= 500) return 'critical';
  return 'error';
}

/**
 * Get user-friendly message based on status code
 */
function getUserMessage(status: number, apiError?: ApiErrorResponse): string {
  // Use backend-provided message if available
  if (apiError?.title) return apiError.title;
  
  // Fallback to standard messages
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return 'You don\'t have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 408:
      return 'Request timeout. Please try again.';
    case 422:
      return 'Validation failed. Please check your input.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error. Our team has been notified.';
    case 502:
      return 'Service temporarily unavailable. Please try again.';
    case 503:
      return 'Service is under maintenance. Please try again later.';
    case 504:
      return 'Gateway timeout. Please try again.';
    default:
      return `Request failed with status ${status}`;
  }
}

/**
 * Get suggested actions based on error type
 */
function getSuggestedActions(status: number, category: ErrorCategory): string[] {
  switch (category) {
    case 'authentication':
      return [
        'Log in again',
        'Check if your session has expired',
        'Clear browser cache and cookies',
      ];
    
    case 'authorization':
      return [
        'Contact your administrator for access',
        'Verify you have the required permissions',
        'Check if your account is active',
      ];
    
    case 'validation':
      return [
        'Review the form for errors',
        'Ensure all required fields are filled',
        'Check that values are in the correct format',
      ];
    
    case 'data':
      return [
        'Verify the resource exists',
        'Check if it has been deleted',
        'Try refreshing the page',
      ];
    
    case 'system':
      return [
        'Try again in a few moments',
        'Contact support if the issue persists',
        'Check the system status page',
      ];
    
    default:
      return [
        'Check your internet connection',
        'Try again in a few moments',
        'Contact support if the issue persists',
      ];
  }
}

/**
 * Create an AppError from an API response
 */
export async function createApiError(
  response: Response,
  endpoint: string,
  method: string
): Promise<AppError> {
  const apiError = await parseApiError(response);
  const category = getErrorCategory(response.status);
  const severity = getErrorSeverity(response.status);
  
  return {
    id: `api-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    message: getUserMessage(response.status, apiError),
    category,
    severity,
    recoverable: response.status < 500 && response.status !== 401,
    technicalDetails: {
      endpoint,
      method,
      statusCode: response.status,
      timestamp: new Date().toISOString(),
      userAgent: window.navigator.userAgent,
      url: window.location.href,
    },
    suggestedActions: getSuggestedActions(response.status, category),
    metadata: {
      apiError,
      statusText: response.statusText,
      type: apiError.type,
      detail: apiError.detail,
      traceId: apiError.traceId,
      errors: apiError.errors,
    },
  };
}

/**
 * Create an AppError from a network error (no response)
 */
export function createNetworkError(
  error: Error,
  endpoint: string,
  method: string
): AppError {
  const isTimeout = error.name === 'AbortError' || error.message.includes('timeout');
  
  return {
    id: `network-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    message: isTimeout 
      ? 'Request timed out. Please check your connection and try again.'
      : 'Unable to connect to the server. Please check your internet connection.',
    category: 'network',
    severity: 'error',
    recoverable: true,
    technicalDetails: {
      endpoint,
      method,
      timestamp: new Date().toISOString(),
      userAgent: window.navigator.userAgent,
      url: window.location.href,
    },
    suggestedActions: [
      'Check your internet connection',
      'Verify the server is running',
      'Try again in a few moments',
      'Contact support if the issue persists',
    ],
    metadata: {
      errorName: error.name,
      errorMessage: error.message,
    },
  };
}

/**
 * Enhanced fetch wrapper with error handling
 * Use this instead of raw fetch() for automatic error handling
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const method = options.method || 'GET';
  
  try {
    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    // Check if response is ok
    if (!response.ok) {
      const apiError = await createApiError(response, endpoint, method);
      throw apiError;
    }
    
    // Parse successful response
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return await response.json();
    }
    
    return await response.text() as any;
  } catch (error) {
    // If it's already an AppError, re-throw it
    if (error && typeof error === 'object' && 'id' in error && 'category' in error) {
      throw error;
    }
    
    // Otherwise, it's a network error
    const networkError = createNetworkError(error as Error, endpoint, method);
    throw networkError;
  }
}
